import React, { useState } from "react";
import McpServerIcon from "./McpServerIcon";
import "./McpPanel.css";

/**
 * Right-column MCP card. Lists the admin-managed MCP connectors (available in
 * every environment) with per-user connect / reauthenticate and a
 * per-environment enable toggle. Affects NEW chats only. Connectors are managed
 * by admins in Admin → MCP Server Management; members connect and toggle only.
 */
export default function McpPanel({
  servers, loading,
  onConnect, onReauth, onDisconnect, onToggle,
}) {
  const [busyUrl, setBusyUrl] = useState(null);
  const [error, setError] = useState(null);

  const guard = (fn) => async (url) => {
    setBusyUrl(url); setError(null);
    try { await fn(url); }
    catch (e) { setError(e.message || "Action failed"); }
    finally { setBusyUrl(null); }
  };

  // For actions not keyed by URL (toggle): still surface failures — an
  // unhandled rejection here means the user clicks and nothing happens.
  const tryAction = (p) => {
    setError(null);
    Promise.resolve(p).catch((e) => setError(e.message || "Action failed"));
  };

  return (
    <div className="ca-mcp">
      {error && <div className="ca-mcp-error">{error}</div>}
      {loading && <div className="ca-mcp-empty">Loading…</div>}
      {!loading && servers.length === 0 && (
        <div className="ca-mcp-empty">No connectors available. Ask an admin to add one.</div>
      )}
      <ul className="ca-mcp-list">
        {servers.map((s) => {
          const busy = busyUrl === s.url;
          return (
            <li key={s.url} className="ca-mcp-row">
              <div className="ca-mcp-row-top">
                <div className="ca-mcp-meta">
                  <McpServerIcon url={s.url} hasIcon={s.has_icon} iconVersion={s.icon_version} />
                  <span className="ca-mcp-name" title={s.url}>{s.name}</span>
                  {s.needs_reauth && <span className="ca-mcp-badge ca-mcp-badge--warn">Reauth</span>}
                </div>
                {s.connected ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={s.enabled}
                    className={`ca-switch${s.enabled ? " on" : ""}`}
                    onClick={() => tryAction(onToggle(s.url, !s.enabled))}
                    title={s.enabled ? "Enabled for new chats" : "Disabled — won't load in new chats"}
                  >
                    <span className="ca-switch-knob" />
                  </button>
                ) : (
                  <button
                    className="ca-btn ca-btn--xs ca-btn--primary"
                    disabled={busy}
                    onClick={() => guard(onConnect)(s.url)}
                  >
                    {busy ? "Connecting…" : "Connect"}
                  </button>
                )}
              </div>

              {s.connected && (
                <div className="ca-mcp-row-actions">
                  <button
                    className="ca-btn ca-btn--xs ca-btn--ghost"
                    disabled={busy}
                    onClick={() => guard(onReauth)(s.url)}
                  >
                    Reauthenticate
                  </button>
                  <button
                    className="ca-btn ca-btn--xs ca-btn--ghost"
                    disabled={busy}
                    onClick={() => guard(onDisconnect)(s.url)}
                  >
                    Disconnect
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
